import type { ComponentProps, ElementType } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components';
import { cn } from '@/shared/utils/styling';

const personalAccountButtonVariants = tv({
  slots: {
    containerSlot: 'flex flex-col items-center',
    buttonSlot:
      'rounded-full flex items-center gap-[1rem] headline4 shadow-none',
    labelSlot: 'text-nowrap',
    columnLabelSlot: 'headline6',
    iconSlot: 'size-[2.4rem]',
  },
  variants: {
    variant: {
      default: {
        buttonSlot: 'md:w-auto py-[0.9rem] pr-[2.4rem] pl-[1.6rem]',
      },
      iconOnly: {
        buttonSlot: 'px-[0.8rem] aspect-square',
        labelSlot: 'hidden',
      },
      column: {
        buttonSlot: 'aspect-square',
        containerSlot: 'space-y-[0.8rem]',
        labelSlot: 'hidden',
      },
    },
    color: {
      default: {
        buttonSlot: 'hover:bg-primary hover:text-primary-foreground',
      },
      secondary: {
        buttonSlot:
          'bg-secondary-bg hover:bg-primary hover:text-primary-foreground',
      },
      inverted: {
        buttonSlot:
          'bg-primary text-primary-foreground hover:bg-secondary-bg hover:text-foreground',
      },
    },
    size: {
      sm: {
        buttonSlot: 'h-[3.2rem] rounded-[1rem] gap-[0.4rem] headline5',
        iconSlot: 'size-[1.6rem]',
      },
      md: {
        buttonSlot: 'h-[4.2rem]',
      },
      lg: {
        buttonSlot: 'h-[5.6rem]',
      },
    },
    allowBorder: {
      true: {
        buttonSlot: 'border-1 border-border',
      },
      false: {
        buttonSlot: 'border-none',
      },
    },
  },
  compoundVariants: [
    {
      variant: 'default',
      size: 'sm',
      class: {
        buttonSlot: 'py-[0.8rem] pr-[1.2rem] pl-[0.8rem]',
      },
    },
  ],
  defaultVariants: {
    variant: 'default',
    size: 'md',
    color: 'default',
    allowBorder: false,
  },
});

type Props = ComponentProps<'button'> &
  VariantProps<typeof personalAccountButtonVariants> & {
    icon: ElementType;
    isRightIconPlacement?: boolean;
    label?: string;
    showTooltip?: boolean;
    className?: string;
    iconClassName?: string;
    allowBorder?: boolean;
  };

const AccountActionButton = ({
  icon,
  isRightIconPlacement,
  label,
  showTooltip = false,
  variant,
  size,
  color,
  iconClassName,
  className,
  allowBorder = false,
  ...props
}: Props) => {
  const IconComponent = icon;

  const { containerSlot, buttonSlot, labelSlot, columnLabelSlot, iconSlot } =
    personalAccountButtonVariants({
      variant,
      size,
      color,
      allowBorder,
    });

  const iconComponent = (
    <IconComponent className={cn(iconSlot(), iconClassName)} />
  );

  const buttonElement = (
    <Button
      variant="outline"
      className={cn(buttonSlot(), className)}
      {...props}>
      {!isRightIconPlacement && iconComponent}

      {label && <span className={labelSlot()}>{label}</span>}

      {isRightIconPlacement && iconComponent}
    </Button>
  );

  if (variant === 'column') {
    return (
      <div className={containerSlot()}>
        {buttonElement}
        <span className={columnLabelSlot()}>{label}</span>
      </div>
    );
  }

  if (showTooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{buttonElement}</TooltipTrigger>
        <TooltipContent side="top" sideOffset={8} className="medium5">
          {label}
        </TooltipContent>
      </Tooltip>
    );
  }

  return buttonElement;
};

export { AccountActionButton };
